import { bookActions } from '@/actions/bookActions';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { getNewExtraTimeAddOns } from '@/components/modules/modals/BookAppointment/BookAppointment.helpers';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import BookingFlowPage from '@/features/booking-flow/BookingFlowPage';
import { BOOKING_FLOW_STEP_SLUGS } from '@/features/booking-flow/constants';
import { deriveStepSelection } from '@/features/booking-flow/deriveStepSelection';
import { useCalendarOffersEmployeePick } from '@/features/booking-flow/useCalendarOffersEmployeePick';
import { buildTargetLocation, goToStep } from '@/features/booking-flow/goToStep';
import { getNextStep, getStepGuardTarget } from '@/features/booking-flow/steps';
import { updateTrackedServiceIds } from '@/features/booking-flow/tracking';
import { useBookingFlowSelection } from '@/features/booking-flow/useBookingFlowSelection';
import withBookingFlowRedesignFlag from '@/features/booking-flow/withBookingFlowRedesignFlag';
import { getAnyEmployee } from '@/features/booking-selection/constants';
import SelectExtraTime from '@/features/booking-selection/views/SelectExtraTime';
import { getPickedEmployee } from '@/helpers/employee';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { _t } from '@/locale';
import { BookState, EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

/**
 * The `/extra-tid` step page (U10): mounts the `SelectExtraTime` view inside the flow
 * shell. No `sideColumn` on purpose (KTD8) — this is a single yes/no question and the
 * focused treatment is deliberate; the selection summary shown on `/tillval` is not
 * repeated here.
 */
const ExtraTimePage = () => {
  const { placeSlugId } = useParams<{ placeSlugId: string }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const book = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const place = book.place;
  useBookingFlowSelection(place);
  const calendarOffersEmployeePick = useCalendarOffersEmployeePick();
  const { attribution } = useBookingFlowChrome();

  if (!place) {
    // useBookingFlowSelection owns the bounded wait and the eventual redirect; this is
    // the same shell-spinner treatment the flag HOC uses while unresolved.
    return (
      <BookingFlowPage title="">
        <LoadingPlaceHolder />
      </BookingFlowPage>
    );
  }

  // R9 guard: unmet prerequisites redirect back; a question the place's services never ask
  // redirects onward. An *answered* question stays renderable (steps.ts REMAIN_CONDITIONS)
  // so a calendar-back rewind can revise it. Reads the URL via deriveStepSelection, never
  // state.book — on a cold load the store is one render behind the URL and a store-read
  // guard would bounce a valid deep link.
  const guardTarget = getStepGuardTarget(
    BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME,
    deriveStepSelection(place, location.search, calendarOffersEmployeePick, user),
  );
  if (guardTarget) {
    return <Redirect to={buildTargetLocation(placeSlugId, guardTarget, location.search)} />;
  }

  const pickedEmployee = book.employee != null ? getPickedEmployee(book.employee, place.employees ?? []) : undefined;

  const onAnswer = (needsExtraTime: boolean) => {
    const requirementValue = needsExtraTime
      ? EXTRA_TIME_REQUIREMENT.NeedsExtraTime
      : EXTRA_TIME_REQUIREMENT.NoExtraTime;
    // @ts-ignore setExtraTimeRequirement is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
    dispatch(bookActions.setExtraTimeRequirement(requirementValue, place.id, user));

    // Reads state.book (hydrated by click-time): "Ja" must append the extra-time add-ons to
    // the same picked-services array the calendar reads, as the modal does.
    const services = book.services ?? [];
    if (needsExtraTime) {
      const extraTimeAddOns = getNewExtraTimeAddOns(services, pickedEmployee ?? getAnyEmployee());
      extraTimeAddOns.forEach((addOn) => {
        dispatch(bookActions.appendExtraService(addOn, services));
      });
      // Sticky-props update only, no event (U15): the appended add-ons change what the
      // calendar books, so checkout_confirmation's service_ids must include them.
      updateTrackedServiceIds([...services, ...extraTimeAddOns].map((service) => String(service.id)));
    } else {
      // Revision support: a "Nej" over an earlier (or stored) "Ja" must remove the
      // materialised extra-time add-ons — hydration re-adds them from the stored answer,
      // and the calendar books whatever state.book holds. Sticky service_ids shrink with
      // them; no event, same as the answer itself (U15).
      const pickedExtraTimeAddOns = services.filter((service) => serviceIsExtraTimeAddOn(service));
      pickedExtraTimeAddOns.forEach((addOn) => {
        // @ts-ignore popService is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
        dispatch(bookActions.popService(addOn.id, book.time?.timestamp, services, book.employee, place, true));
      });
      if (pickedExtraTimeAddOns.length > 0) {
        updateTrackedServiceIds(
          services.filter((service) => !serviceIsExtraTimeAddOn(service)).map((service) => String(service.id)),
        );
      }
    }

    goToStep(
      history,
      placeSlugId,
      getNextStep(BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME, {
        place,
        services,
        employee: book.employee,
        extraTimeRequirement: requirementValue,
        user,
        calendarOffersEmployeePick,
      }),
      { et: needsExtraTime ? '1' : '0' },
    );
  };

  return (
    <BookingFlowPage
      title={_t('features.bookingSelection.SelectExtraTime.title')}
      subtitle={place.about?.name}
      merchantLogoUrl={place.about?.profileImage}
      emitStepShown>
      <SelectExtraTime merchantLogoUrl={place.about?.profileImage} placeName={place.about?.name} onAnswer={onAnswer} />
      {attribution ? <div className="mt-xl lg:mt-2xl">{attribution('column')}</div> : null}
    </BookingFlowPage>
  );
};

export default withBookingFlowRedesignFlag(ExtraTimePage);
